import {m as motion} from 'framer-motion'
import {graphql} from 'gatsby'
import React from 'react'
import styled from 'styled-components'

import Button from '@/components/button'
import PrismicLink from '@/components/prismic-link'
import {Text} from '@/components/text'
import type {PrismicLinkFragment} from '@/graphql-types'
import {useCountdown} from '@/hooks/use-countdown'
import {useGlobalStrings} from '@/providers/global-strings'
import {Section} from '@/styles/grid'
import {tokens, radii, colors} from '@/theme'
import mq from '@/utils/media-query'

type Props = {
    heading: string
    dateTime: string
    descriptionText: string
    ctaText?: string
    ctaLink?: PrismicLinkFragment
}

export const Countdown = ({dateTime, heading, descriptionText, ctaLink, ctaText}: Props) => {
    const globalStrings = useGlobalStrings()
    const {days, hours, minutes, hasMounted} = useCountdown(dateTime)

    return (
        <StyledSection>
            <Timer
                dateTime={dateTime}
                initial={{opacity: 0}}
                animate={{opacity: hasMounted ? 1 : 0}}
            >
                <TimerItem value={days} label={globalStrings.days} />
                <TimerItem value={hours} label={globalStrings.hours} />
                <TimerItem value={minutes} label={globalStrings.minutes} />
            </Timer>

            {heading && (
                <Text as="h2" $variant="heading-base">
                    {heading}
                </Text>
            )}

            <Text $variant="paragraph-large" $color="muted" $center style={{maxWidth: 840}}>
                {descriptionText}
            </Text>

            {ctaText && ctaLink && (
                <Button as={PrismicLink} {...ctaLink}>
                    {ctaText}
                </Button>
            )}
        </StyledSection>
    )
}

type TimerItemProps = {
    value: number
    label: string
}

const TimerItem = ({value, label}: TimerItemProps) => {
    return (
        <TimerBox>
            <Text as="span" $variant="hero" $color="inverted" translate="no">
                {value.toString().padStart(2, '0')}
            </Text>

            <Text as="span" $variant="paragraph-large" $color="inverted">
                {label}
            </Text>
        </TimerBox>
    )
}

const mobileBreakpoint = 'sm'

const StyledSection = styled(Section)`
    display: grid;
    row-gap: ${tokens.spacing24};
    justify-items: center;

    ${mq.min(mobileBreakpoint)} {
        row-gap: ${tokens.spacing40};
    }
`

const Timer = styled(motion.time)`
    display: grid;
    row-gap: ${tokens.spacing20};
    justify-self: stretch;

    ${mq.min(mobileBreakpoint)} {
        grid-template-columns: repeat(3, 1fr);
        column-gap: ${tokens.spacing40};
    }
`

const TimerBox = styled.span`
    display: grid;
    padding: ${tokens.spacing20};
    background: ${colors.background.tertiary};
    border-radius: ${radii.arc20};
    row-gap: ${tokens.spacing10};
    justify-items: center;

    ${mq.min(mobileBreakpoint)} {
        padding: ${tokens.spacing40};
    }
`

export const countdownFragment = graphql`
    fragment Countdown on PrismicSlicePageDataBodyCountdown {
        __typename
        primary {
            # Aliasing needed to get around conflicting fields error (I think conflicting with other slices.. seems like a bug)
            headingText: heading
            description_text
            target_date_time
            cta_link {
                ...PrismicLink
            }
            cta_text
        }
    }
`
